import React, { useEffect, useState, useRef } from 'react';
import { Search, Send, Clock, User as UserIcon, MessageSquare } from 'lucide-react';
import { 
  getConversations, 
  getConversationMessages, 
  sendConversationMessage, 
  markConversationRead 
} from '../services/api';

interface TargetUser {
  id: string;
  name: string;
  avatar_url: string | null;
  is_verified: boolean;
}

interface LastMessage {
  content: string;
  sender_id: string;
  created_at: string;
}

interface Conversation {
  id: string;
  status: string;
  initiator_id: string;
  last_message_at: string;
  created_at: string;
  target_user: TargetUser | null;
  unread_count: number;
  last_message: LastMessage | null;
}

interface Message {
  id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

const Messages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const [toast, setToast] = useState<{message: string, type: 'error' | 'success'} | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Poll for new messages periodically if a thread is open, or just fetch once
  useEffect(() => {
    if (selectedId) {
      fetchThread(selectedId);
      markRead(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchConversations = async () => {
    try {
      setLoadingList(true);
      const res = await getConversations();
      // Ensure we get data safely
      setConversations(Array.isArray(res) ? res : []); 
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchThread = async (id: string) => {
    try {
      setLoadingThread(true);
      const res = await getConversationMessages(id);
      setMessages(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingThread(false);
    }
  };

  const markRead = async (id: string) => {
    try {
      await markConversationRead(id);
      setConversations(prev => 
        prev.map(c => c.id === id ? { ...c, unread_count: 0 } : c)
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedId || !replyText.trim()) return;

    const currentText = replyText.trim();
    setReplyText('');
    setSending(true);

    try {
      const res = await sendConversationMessage(selectedId, currentText);
      const newMsg = res.data || res; // API returns { data: {...} } or {...}
      if (newMsg && newMsg.id) {
        setMessages(prev => [...prev, newMsg]);
      } else {
        fetchThread(selectedId);
      }
      showToast('Message sent!');
    } catch (err) {
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (isoString: string) => {
    const d = new Date(isoString);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString: string) => {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    today.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) {
      return 'Yesterday';
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const truncate = (str: string, len: number) => {
    if (!str) return '';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  const filteredConversations = conversations.filter(c => {
    if (!searchQuery) return true;
    const name = c.target_user?.name || 'Unknown';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const selectedConversation = conversations.find(c => c.id === selectedId);
  const targetName = selectedConversation?.target_user?.name || 'Buyer';

  // To determine if a message is from the seller, we need current user ID.
  // Since we don't have user context easily here without a provider, we can infer it:
  // If the target_user is present, the sender_id of the message, if NOT target_user.id, must be the seller.
  const targetUserId = selectedConversation?.target_user?.id;

  return (
    <div className="max-w-6xl mx-auto h-[80vh] flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
      {/* Toast */}
      {toast && (
        <div className={`absolute top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-lg shadow-lg text-white font-medium z-50 transition-opacity ${toast.type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
          {toast.message}
        </div>
      )}

      {loadingList && conversations.length === 0 ? (
        <div className="flex-1 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-900"></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="flex-1 flex flex-col justify-center items-center bg-gray-50/50">
          <div className="w-20 h-20 rounded-2xl bg-[#F1EFE8] flex items-center justify-center mb-6">
            <span className="text-4xl text-[#085041]">💬</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No messages yet!</h2>
          <p className="text-gray-500">When buyers contact you, conversations will appear here.</p>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden h-full">
          {/* LEFT PANEL: Conversations List */}
          <div className="w-full md:w-[320px] shrink-0 border-r border-gray-100 flex flex-col bg-white">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-xl font-bold text-teal-900 mb-4">Messages</h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search buyers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all text-sm"
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No conversations match your search.
                </div>
              ) : (
                filteredConversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedId(conv.id)}
                    className={`w-full text-left p-4 border-b border-gray-50 transition-colors hover:bg-gray-50 relative flex items-start gap-3 ${selectedId === conv.id ? 'bg-gray-50/80 border-l-4 border-l-[#085041] pl-3' : 'border-l-4 border-l-transparent'}`}
                  >
                    <div className="w-12 h-12 bg-[#F1EFE8] rounded-full shrink-0 flex items-center justify-center overflow-hidden border border-gray-200">
                      {conv.target_user?.avatar_url ? (
                        <img src={conv.target_user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        <UserIcon className="text-gray-400" size={24} />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0 pr-6">
                      <div className="flex justify-between items-baseline mb-1">
                        <h4 className="font-semibold text-gray-900 truncate pr-2">
                          {conv.target_user?.name || 'Unknown User'}
                        </h4>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatDate(conv.last_message_at || conv.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                        {truncate(conv.last_message?.content || 'Started a conversation', 40)}
                      </p>
                    </div>

                    {conv.unread_count > 0 && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 bg-[#D85A30] text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                        {conv.unread_count}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* RIGHT PANEL: Message Thread */}
          <div className="flex-1 flex flex-col bg-[#F9F9F8] relative">
            {!selectedId ? (
              <div className="flex-1 flex flex-col justify-center items-center text-gray-400">
                <MessageSquare size={48} className="mb-4 opacity-50" />
                <p>Select a conversation to start messaging</p>
              </div>
            ) : (
              <>
                {/* Thread Header */}
                <div className="h-16 px-6 border-b border-gray-200 flex items-center bg-white shrink-0 shadow-sm z-10">
                  <div className="w-10 h-10 bg-[#F1EFE8] rounded-full flex items-center justify-center mr-3 overflow-hidden border border-gray-200">
                    {selectedConversation?.target_user?.avatar_url ? (
                      <img src={selectedConversation.target_user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon className="text-gray-400" size={20} />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{targetName}</h3>
                    {selectedConversation?.target_user?.is_verified && (
                      <span className="text-xs text-[#1D9E75] font-medium flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1D9E75]"></span> Verified Buyer
                      </span>
                    )}
                  </div>
                </div>

                {/* Thread Messages */}
                <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                  {loadingThread ? (
                    <div className="flex justify-center items-center flex-1">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-900"></div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-400 mt-10">
                      No messages yet. Say hello!
                    </div>
                  ) : (
                    messages.map((msg, idx) => {
                      const isSeller = targetUserId ? msg.sender_id !== targetUserId : false;
                      const showAvatar = !isSeller && (idx === 0 || messages[idx - 1].sender_id !== msg.sender_id);
                      
                      return (
                        <div key={msg.id || idx} className={`flex ${isSeller ? 'justify-end' : 'justify-start'} mb-1`}>
                          <div className={`flex items-end max-w-[70%] gap-2 ${isSeller ? 'flex-row-reverse' : 'flex-row'}`}>
                            
                            {/* Avatar for buyer only */}
                            {!isSeller && (
                              <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center overflow-hidden bg-gray-200 border border-white">
                                {showAvatar ? (
                                  selectedConversation?.target_user?.avatar_url ? (
                                    <img src={selectedConversation.target_user.avatar_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <UserIcon className="text-gray-400" size={16} />
                                  )
                                ) : (
                                  <div className="w-8 h-8 bg-transparent"></div>
                                )}
                              </div>
                            )}

                            {/* Bubble */}
                            <div 
                              className={`px-5 py-3 rounded-2xl relative ${
                                isSeller 
                                  ? 'bg-[#085041] text-white rounded-br-sm shadow-sm' 
                                  : 'bg-[#F1EFE8] text-gray-800 rounded-bl-sm border border-gray-200 shadow-sm'
                              }`}
                            >
                              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              <span 
                                className={`text-[10px] mt-1.5 flex items-center justify-end font-medium opacity-70 ${
                                  isSeller ? 'text-teal-100' : 'text-gray-500'
                                }`}
                              >
                                {formatTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-gray-200 shrink-0">
                  <form onSubmit={handleSend} className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type a reply..."
                      disabled={sending}
                      className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all"
                    />
                    <button
                      type="submit"
                      disabled={!replyText.trim() || sending}
                      className="w-12 h-12 bg-[#085041] text-white rounded-full flex items-center justify-center hover:bg-[#063b30] transition-colors disabled:opacity-50 disabled:hover:bg-[#085041] shrink-0"
                    >
                      <Send size={20} className={replyText.trim() ? "ml-0.5" : ""} />
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
